/**
 * The Long Ledger — dates, months and seasons.
 *
 * ── The granularity, and why ──────────────────────────────────────────────
 *
 * A day is **forty seconds of simulated settlement time**. It is not a wave, and
 * it is no longer a campaign turn either, because there are no longer any turns
 * to count.
 *
 * The settlement is real-time at `TICK_RATE` ticks a second. A build phase is
 * 150 seconds (300 for the first) and a siege runs anywhere from thirty seconds
 * to the 240-second breaking point in `stepDirector`, so one wave cycle is
 * roughly four minutes of simulated time and *varies*.
 *
 * Pinning the day to a wave was never an option:
 *
 *   - "A day is a wave" makes a season the length of a run, and worse, makes the
 *     length of a day depend on how quickly the player presses the horn. Winter
 *     would arrive earlier for a fast player, which is exactly backwards.
 *
 * So the day is tied to simulated seconds, which every part of the game has, and
 * everything else is stated as a multiple of it:
 *
 *   1 day            = 40 simulated seconds (`DAY_SECONDS`)
 *   1 month          = 7 days / 280 seconds (~4.7 minutes, ~1 wave cycle)
 *   1 season         = 2 months             (~9 minutes, ~2 wave cycles)
 *   1 year           = 8 months / 56 days   (~37 minutes, ~9 waves)
 *
 * A run therefore opens in Mudmonth, meets its first Hardfrost somewhere around
 * wave six or seven — after there has been time to raise a granary and a bakery
 * — and sees four or five winters across a forty-wave run.
 *
 * ── The month is now a duration, and it is the realm's heartbeat ──────────
 *
 * A campaign turn used to *be* a month: `syncCalendarToTurn` pulled the date
 * monotonically off `campaign.turn`, so the realm's date moved when — and only
 * when — somebody pressed a button. With turns gone that direction reverses.
 * **A month is `MONTH_SECONDS` of simulated time, and the council sits when the
 * month turns.** `stepRealm` in `campaign/turn.ts` reads `calendarDay` and does
 * the rest; nothing in the campaign holds a clock of its own, which is the whole
 * reason there is nothing to drift.
 *
 * "One turn is one month" is therefore still exactly true, and is now true by
 * construction rather than by a synchronising function that had to be called.
 *
 * ── What holds the date ───────────────────────────────────────────────────
 *
 * Two scalars on the World: `calendarDay`, whole days since the run began, and
 * `calendarT`, seconds accrued into the current day. Both are hashed and saved.
 * Month, season and year are *derived* by division and are not stored — they
 * cannot drift from the day count if they do not exist.
 *
 * ── The seam that used to be left open, and is now nearly closed ──────────
 *
 * A campaign is two live worlds (see HANDOFF.md): the realm and the capital.
 * They still keep separate dates, exactly as they keep separate `tick`s — but
 * both are now stepped every tick by `stepGame`, from the same `dt`, starting
 * from the same day zero, so in practice they agree to the tick.
 *
 * The one thing that can part them is a battle. Fighting an assault on your own
 * capital holds the realm and not the city (the city *is* the battlefield), so
 * the realm's date lags the capital's by however long the assault took. That is
 * the intended shape: the world is not allowed to move while you are busy
 * defending your one piece of it.
 */

import { LogTone, pushLog } from '../events.ts';
import type { ResourceKey } from '../types.ts';
import type { World } from '../world.ts';

/** Simulated seconds in a day. See the exchange rate above. */
export const DAY_SECONDS = 40;
export const DAYS_PER_MONTH = 7;
export const MONTHS_PER_SEASON = 2;
export const MONTHS_PER_YEAR = 8;
export const DAYS_PER_YEAR = DAYS_PER_MONTH * MONTHS_PER_YEAR;

/**
 * A month, in simulated seconds. 280 at normal speed.
 *
 * This is the number the strategic layer is measured in now that it has no
 * turns: a march of two months is 560 seconds of walking, a siege of three is a
 * quarter of an hour of sitting in the mud, and the warning that a host is on
 * the road is worth at least one of these before it lands. Stated here, once,
 * rather than in `campaign/types.ts`, so there is exactly one place where the
 * length of realm time is decided.
 */
export const MONTH_SECONDS = DAYS_PER_MONTH * DAY_SECONDS;

/**
 * The months, as a tired clerk writes them: what the month is *for*, plus the
 * word "month", because that is how a ledger is kept and not how a bard sings.
 */
export const MONTH_NAMES = [
  'Mudmonth',
  'Seedmonth',
  'Grassmonth',
  'Haymonth',
  'Reapmonth',
  'Stubblemonth',
  'Frostmonth',
  'Deadmonth',
] as const;

export interface SeasonDef {
  id: string;
  name: string;
  /** Multiplier on grain coming off the fields. */
  field: number;
  /** Multiplier on timber, stone and iron — the work done outdoors. */
  work: number;
  /** Multiplier on what the settlement eats. */
  upkeep: number;
  /** One line, exact, for the HUD. The rules-text rule applies here too. */
  rules: string;
  /** The line pushed to the log when the season turns. */
  turns: string;
}

/**
 * Four seasons, two months each, one or two mechanical effects apiece.
 *
 * The arc is the point: Tally is the only window where a settlement can get
 * meaningfully ahead on food, and Hardfrost is the bill. A town that spent its
 * harvest on walls will find out in Frostmonth, which is early enough to be a
 * lesson and late enough to have been a choice.
 */
export const SEASONS: SeasonDef[] = [
  {
    id: 'thaw',
    name: 'Thaw',
    field: 0.75,
    work: 1,
    upkeep: 1,
    rules: 'Fields give a quarter less. The seed is in the ground and is not yet anything.',
    turns: 'Mudmonth. The roads go soft and the granary does not refill itself.',
  },
  {
    id: 'longsun',
    name: 'Long Sun',
    field: 1,
    work: 1.15,
    upkeep: 1,
    rules: 'Timber, stone and iron come in 15% faster. Long days, and nobody stops at dusk.',
    turns: 'Grassmonth. Long light. Work the pits and the woods while it lasts.',
  },
  {
    id: 'tally',
    name: 'Tally',
    field: 1.75,
    work: 1,
    upkeep: 1,
    rules: 'Fields give three quarters more. This is the only time the granary gets ahead.',
    turns: 'Reapmonth. Everything that will be counted this year is counted now.',
  },
  {
    id: 'hardfrost',
    name: 'Hardfrost',
    field: 0.15,
    work: 0.85,
    upkeep: 1.35,
    rules: 'Fields give almost nothing, the town eats a third more, and the pits run slow.',
    turns: 'Frostmonth. Nothing grows now. What you have is what you have.',
  },
];

// ── Reading the date ───────────────────────────────────────────────────────

export interface CalendarDate {
  /** Whole days since the run began. */
  absoluteDay: number;
  /** 1-based day within the month. */
  day: number;
  /** 0-based month within the year. */
  monthIdx: number;
  month: string;
  /** 1-based. Year 1 is the year the Hearth was lit. */
  year: number;
  seasonIdx: number;
  season: SeasonDef;
}

export function dateOf(w: World): CalendarDate {
  return dateFromDay(w.calendarDay);
}

export function dateFromDay(absoluteDay: number): CalendarDate {
  const d = Math.max(0, Math.floor(absoluteDay));
  const dayOfYear = d % DAYS_PER_YEAR;
  const monthIdx = (dayOfYear / DAYS_PER_MONTH) | 0;
  const seasonIdx = (monthIdx / MONTHS_PER_SEASON) | 0;
  return {
    absoluteDay: d,
    day: (dayOfYear % DAYS_PER_MONTH) + 1,
    monthIdx,
    month: MONTH_NAMES[monthIdx],
    year: ((d / DAYS_PER_YEAR) | 0) + 1,
    seasonIdx,
    season: SEASONS[seasonIdx],
  };
}

export function seasonOfDay(absoluteDay: number): SeasonDef {
  return dateFromDay(absoluteDay).season;
}

export function season(w: World): SeasonDef {
  return seasonOfDay(w.calendarDay);
}

/** `Reapmonth 4` — short enough to sit on the HUD's clock line. */
export function formatDateShort(w: World): string {
  const d = dateOf(w);
  return `${d.month} ${d.day}`;
}

/** `The 4th of Reapmonth, Year 1 — Tally` — for a tooltip that has the room. */
export function formatDateLong(w: World): string {
  return formatDayLong(w.calendarDay);
}

/** The same, for a day the world is not standing on yet — the console uses it. */
export function formatDayLong(absoluteDay: number): string {
  const d = dateFromDay(absoluteDay);
  return `The ${ordinal(d.day)} of ${d.month}, Year ${d.year} — ${d.season.name}`;
}

function ordinal(n: number): string {
  const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
}

/** 0..1 through the current season, for a progress bar or a warning. */
export function seasonProgress(w: World): number {
  const span = DAYS_PER_MONTH * MONTHS_PER_SEASON;
  const into = w.calendarDay % span;
  return (into + w.calendarT / DAY_SECONDS) / span;
}

/** Days until the season turns, rounded up. Winter is worth counting toward. */
export function daysToNextSeason(w: World): number {
  const span = DAYS_PER_MONTH * MONTHS_PER_SEASON;
  return span - (w.calendarDay % span);
}

// ── What a season does ─────────────────────────────────────────────────────

const WORK_RESOURCES: ResourceKey[] = ['timber', 'stone', 'iron'];

/**
 * The multiplier on a producer's output of `k`, this season.
 *
 * Grain moves with the fields; the raw extraction resources move with the
 * length of the working day. Planks, bread, coin and souls do not move at all —
 * a sawmill, an oven, a counting house and a reliquary are all indoors, and a
 * bakery that stopped in winter would punish the exact answer to winter.
 */
export function seasonYield(w: World, k: ResourceKey): number {
  const s = season(w);
  if (k === 'grain') return s.field;
  if (WORK_RESOURCES.includes(k)) return s.work;
  return 1;
}

/** The multiplier on what the settlement eats, this season. */
export function seasonUpkeep(w: World): number {
  return season(w).upkeep;
}

// ── Advancing it ───────────────────────────────────────────────────────────

/**
 * Time. Called every tick of every world that is not a battlefield — the
 * settlement's and the realm's alike — so days pass during a siege exactly as
 * they do during a build phase, and pass on the world map exactly as they do in
 * the city. A wave does not happen outside the year, and neither does a march.
 */
export function stepCalendar(w: World, dt: number): void {
  w.calendarT += dt;
  if (w.calendarT < DAY_SECONDS) return;

  const wasSeason = season(w);
  let guard = 0;
  while (w.calendarT >= DAY_SECONDS && guard++ < 4096) {
    w.calendarT -= DAY_SECONDS;
    w.calendarDay++;
  }
  const now = season(w);
  if (now !== wasSeason) announceSeason(w, now);
}

/**
 * Whole months since the run began. Month 0 is the first one.
 *
 * The realm's heartbeat: `stepRealm` compares this against the turn counter and
 * sits the council whenever it has moved on. Derived by division rather than
 * stored, for the same reason the season is — a counter that cannot exist cannot
 * disagree with the day it was supposed to be counting.
 */
export function monthsElapsed(w: World): number {
  return Math.floor(Math.max(0, w.calendarDay) / DAYS_PER_MONTH);
}

/** Days until the month turns and the council sits. Never zero — 7 means "today". */
export function daysToNextMonth(w: World): number {
  return DAYS_PER_MONTH - (Math.max(0, w.calendarDay) % DAYS_PER_MONTH);
}

/** Simulated seconds until the council sits, for a readout that has the room. */
export function secondsToNextMonth(w: World): number {
  return Math.max(0, daysToNextMonth(w) * DAY_SECONDS - w.calendarT);
}

function announceSeason(w: World, s: SeasonDef): void {
  pushLog(w.events, s.turns, s.id === 'hardfrost' ? LogTone.Bad : LogTone.Lore);
}

/**
 * `Cmd.DebugSetDate`. Jump the ledger, for the console and for tests that need
 * to stand in a particular month without playing thirty minutes to reach it.
 */
export function setDate(w: World, absoluteDay: number): void {
  const day = Math.max(0, Math.floor(absoluteDay));
  const wasSeason = season(w);
  w.calendarDay = day;
  w.calendarT = 0;
  const now = season(w);
  if (now !== wasSeason) announceSeason(w, now);
}

/** The first day of the next month with this name, at or after today. */
export function dayOfNextMonth(w: World, monthName: string): number {
  const idx = MONTH_NAMES.findIndex((m) => m.toLowerCase() === monthName.toLowerCase());
  if (idx < 0) return -1;
  // Parenthesised on purpose: `a / b | 0 * c` is not what it looks like, and
  // that exact trap has shipped in this codebase once already.
  const yearStart = ((w.calendarDay / DAYS_PER_YEAR) | 0) * DAYS_PER_YEAR;
  const candidate = yearStart + idx * DAYS_PER_MONTH;
  return candidate > w.calendarDay ? candidate : candidate + DAYS_PER_YEAR;
}
